import { Bell, Heart, ShoppingBag, Star, Check } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import "./latest.css";
import {
  addToWishlist,
  removeFromWishlist,
} from "../../redux/reducers/wishlistSlice";
import { addToCartAsync, removeFromCartAsync } from "../../redux/reducers/cart";
import { cldUrl } from "../../utils/cloudinary";

/* ── Size ordering: S → M → L → XL …, numeric sizes after letters ── */
const SIZE_ORDER = ["XXS", "XS", "S", "M", "L", "XL", "XXL", "XXXL"];

export const sizeRank = (size) => {
  const i = SIZE_ORDER.indexOf(String(size).toUpperCase());
  if (i >= 0) return i;
  const n = parseFloat(size);
  return Number.isNaN(n) ? 1000 : 100 + n;
};

const byWord = (a, b) => sizeRank(a) - sizeRank(b); // for plain strings
const byObj = (a, b) => sizeRank(a.size) - sizeRank(b.size); // for { size } objects

function StarRating({ rating, count }) {
  return (
    <div className="pc-rating">
      <div className="pc-stars">
        {[0, 1, 2, 3, 4].map((i) => {
          const fillPct = Math.max(0, Math.min(1, rating - i)) * 100;
          return (
            <span className="pc-star" key={i}>
              <span className="pc-star-outline">
                <Star size={13} strokeWidth={1.5} />
              </span>
              <span className="pc-star-fill" style={{ width: `${fillPct}%` }}>
                <Star size={13} strokeWidth={1.5} fill="currentColor" />
              </span>
            </span>
          );
        })}
      </div>
      <span className="pc-rating-count">({count})</span>
    </div>
  );
}

export function ProductCard({
  id,
  slug,
  image: fallbackImage,
  title,
  price,
  originalPrice,
  brand,
  currency,
  badge,
  promo,
  colors,
  variants = [],
  tag,
  tone,
  rating,
  count,
  sizesAvailable: fallbackAvailable = [],
  sizesNotify: fallbackNotify = [],
  onPickSize,
}) {
  const navigate = useNavigate();
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [justAdded, setJustAdded] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const dispatch = useDispatch();
  const { wishlist } = useSelector((state) => state.wishlist);
  const { cart } = useSelector((state) => state.cart);

  const currentVariant = variants[selectedIdx] || null;
  const currentImage = currentVariant?.images?.[0]?.url || fallbackImage;
  const imgRef = useRef(null);

  // Handles cached images, and resets the skeleton when the color swatch changes
  useEffect(() => {
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth > 0) setImageLoaded(true);
    else setImageLoaded(false);
  }, [currentImage]);

  const reduxWishlisted = wishlist.some(
    (item) => item._id?.toString() === id?.toString(),
  );
  const [isWishlisted, setIsWishlisted] = useState(reduxWishlisted);
  useEffect(() => {
    setIsWishlisted(reduxWishlisted);
  }, [reduxWishlisted]);

  const handleWishlistToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsWishlisted((prev) => !prev);
    if (isWishlisted) {
      dispatch(removeFromWishlist(id));
    } else {
      dispatch(
        addToWishlist({
          _id: id,
          title,
          slug,
          image: currentImage || fallbackImage,
          price,
          originalPrice,
        }),
      );
    }
  };

  // Sorted once here, so everything derived from it is in order too
  const currentSizes = [...(currentVariant?.sizes || [])].sort(byObj);
  const currentAvailable = currentSizes
    .filter((s) => s.stock > 0)
    .map((s) => s.size);
  const currentNotify = currentSizes
    .filter((s) => s.stock <= 0)
    .map((s) => s.size);

  // Fallback lists come straight from props, so sort them as well
  const sizesAvailable =
    currentAvailable.length > 0
      ? currentAvailable
      : [...fallbackAvailable].sort(byWord);
  const sizesNotify =
    currentNotify.length > 0 ? currentNotify : [...fallbackNotify].sort(byWord);

  const discount = originalPrice
    ? Math.round(
        ((Number(originalPrice) - Number(price)) / Number(originalPrice)) * 100,
      )
    : 0;

  /* ─────────────── Cart helpers ─────────────── */
  const isSizeInCart = (size) => {
    if (!currentVariant?.color?._id) return false;
    return cart.some(
      (item) =>
        item.productId === id &&
        item.color?._id === currentVariant.color._id &&
        item.size === size,
    );
  };

  const handleSizeClick = (sizeObj, e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!currentVariant?.color?._id) return;

    const inCart = isSizeInCart(sizeObj.size);
    if (inCart) {
      dispatch(removeFromCartAsync(id, currentVariant.color._id, sizeObj.size));
    } else {
      dispatch(
        addToCartAsync({
          productId: id,
          name: title,
          slug,
          brand,
          basePrice: price,
          originalPrice,
          currency,
          image: currentImage || fallbackImage,
          color: currentVariant.color,
          size: sizeObj.size,
          sku: sizeObj.sku,
          stock: sizeObj.stock,
          quantity: 1,
        }),
      );
      setJustAdded(sizeObj.size);
      setTimeout(() => {
        setJustAdded((prev) => (prev === sizeObj.size ? null : prev));
      }, 1200);
    }
  };

  const openSizeSheet = () => {
    if (!onPickSize || !currentVariant) return;
    onPickSize({
      id,
      title,
      slug,
      brand,
      price,
      originalPrice,
      currency,
      image: currentImage || fallbackImage,
      variant: currentVariant,
    });
  };
  /* ─────────────────────────────────────────── */

  return (
    <div className="pc-card">
      <div
        className={`pc-image-wrap ${!currentImage ? `pc-tone-${tone}` : ""} ${
          currentImage && !imageLoaded ? "pc-image-loading" : ""
        }`}
        tabIndex={0}
        onClick={() => navigate(`/product/${slug}`)}
      >
        {currentImage && (
          <img
            ref={imgRef}
            key={currentImage}
            src={cldUrl(currentImage, 480)}
            srcSet={[240, 360, 480, 640, 800]
              .map((w) => `${cldUrl(currentImage, w)} ${w}w`)
              .join(", ")}
            sizes="(max-width: 768px) 180px, 315px"
            width={720}
            height={1000}
            alt={title}
            className="pc-image"
            loading="lazy"
            decoding="async"
            onLoad={() => setImageLoaded(true)}
            onError={() => setImageLoaded(true)}
          />
        )}

        {/* Skeleton shimmer */}
        {currentImage && !imageLoaded && (
          <div className="pc-skeleton">
            <div className="pc-skeleton-shimmer" />
          </div>
        )}

        {discount > 0 && imageLoaded && (
          <span className="pc-discount-badge">-{discount}%</span>
        )}

        {!currentImage && (
          <svg
            className="pc-placeholder-icon"
            width="70"
            height="70"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M8 3h8l2 3-3 2v13H7V8L4 6l4-3Z"
              stroke="currentColor"
              strokeWidth="1.3"
              strokeLinejoin="round"
            />
          </svg>
        )}

        {badge && imageLoaded && (
          <span className={`pc-badge pc-badge--${badge.tone}`}>
            {badge.label}
          </span>
        )}

        {tag && imageLoaded && (
          <span className="pc-tag">
            <svg width="12" height="11" viewBox="0 0 12 11" fill="none">
              <rect x="0" y="0" width="1.3" height="11" fill="currentColor" />
              <rect x="2.6" y="0" width="0.9" height="11" fill="currentColor" />
              <rect x="4.4" y="0" width="1.8" height="11" fill="currentColor" />
              <rect x="7.4" y="0" width="0.9" height="11" fill="currentColor" />
              <rect x="9" y="0" width="1.3" height="11" fill="currentColor" />
              <rect x="11" y="0" width="0.9" height="11" fill="currentColor" />
            </svg>
            {tag}
          </span>
        )}

        {/* Mobile / tablet basket */}
        {onPickSize && (
          <button
            className="pc-basket-btn"
            aria-label={`Select size for ${title}`}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              openSizeSheet();
            }}
          >
            <ShoppingBag size={15} strokeWidth={1.7} />
          </button>
        )}

        {/* Desktop hover sizes */}
        <div className="pc-addtobag">
          <p className="pc-addtobag-title">Add to Bag</p>

          {currentSizes.filter((s) => s.stock > 0).length > 0 ? (
            <div className="pc-size-grid">
              {currentSizes
                .filter((s) => s.stock > 0)
                .map((sizeObj) => {
                  const inCart = isSizeInCart(sizeObj.size);
                  const flash = justAdded === sizeObj.size;
                  return (
                    <button
                      key={sizeObj.size}
                      className={`pc-size-btn ${
                        inCart ? "pc-size-btn--incart" : ""
                      } ${flash ? "pc-size-btn--flash" : ""}`}
                      type="button"
                      onClick={(e) => handleSizeClick(sizeObj, e)}
                      title={
                        inCart ? "Click to remove from cart" : "Add to cart"
                      }
                    >
                      {inCart || flash ? (
                        <Check size={11} strokeWidth={2.5} />
                      ) : (
                        sizeObj.size
                      )}
                    </button>
                  );
                })}
            </div>
          ) : (
            sizesAvailable.length > 0 && (
              <div className="pc-size-grid">
                {sizesAvailable.map((s) => (
                  <button
                    key={s}
                    className="pc-size-btn pc-size-btn--disabled"
                    disabled
                  >
                    {s}
                  </button>
                ))}
              </div>
            )
          )}

          {sizesNotify.length > 0 && (
            <>
              <p className="pc-notify-title">Get Notified when available</p>
              <div className="pc-size-row-notify">
                {sizesNotify.map((s) => (
                  <button
                    key={s}
                    className="pc-size-btn pc-size-btn--notify"
                    type="button"
                  >
                    <Bell size={11} strokeWidth={2} />
                    {s}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      <div className="pc-details">
        <div className="pc-title-row">
          <Link to={`/product/${slug}`} className="pc-title-link">
            <p className="pc-title" title={title}>
              {title}
            </p>
          </Link>

          <button
            className={`pc-heart${isWishlisted ? " pc-heart--active" : ""}`}
            aria-label={
              isWishlisted ? "Remove from wishlist" : "Add to wishlist"
            }
            type="button"
            onClick={handleWishlistToggle}
          >
            <Heart
              size={18}
              strokeWidth={1.5}
              fill={isWishlisted ? "currentColor" : "none"}
            />
          </button>
        </div>

        <div className="pc-price">
          <span className="pc-price-current">kes {price}</span>
          {originalPrice && (
            <span className="pc-price-original">kes {originalPrice}</span>
          )}
          {discount > 0 && (
            <span className="pc-price-discount">{discount}%</span>
          )}
        </div>

        {rating != null && <StarRating rating={rating} count={count} />}

        <div className="pc-swatches">
          {colors.map((c, i) => (
            <button
              key={c + i}
              type="button"
              className={`pc-swatch ${
                i === selectedIdx ? "pc-swatch--selected" : ""
              }`}
              style={{ backgroundColor: c }}
              onClick={() => setSelectedIdx(i)}
              aria-label={`Select ${variants[i]?.color?.name || c}`}
              title={variants[i]?.color?.name || c}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
